import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Separator,
  Switch,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  BellIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  LogOutIcon,
} from 'lucide-react'

import { AppHeader } from '@/components/app-header'
import { useAuth } from '@/hooks/use-auth'
import { usePushNotifications } from '@/hooks/use-push'
import type { Membership } from '@/lib/api-types'

export const Route = createFileRoute('/_app/ajustes')({
  component: AjustesPage,
})

function getInitials(value?: string | null) {
  if (!value) return 'U'
  return value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function OrganizationAvatar({
  name,
  logo,
  className,
}: {
  name?: string | null
  logo?: string | null
  className?: string
}) {
  return (
    <Avatar className={cn('size-9 rounded-lg', className)}>
      {logo ? <AvatarImage src={logo} alt="" /> : null}
      <AvatarFallback className="bg-primary/10 text-primary rounded-lg text-xs font-semibold">
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  )
}

function OrganizationSwitcher({
  memberships,
  currentOrgId,
  onSwitchOrg,
}: {
  memberships: Membership[]
  currentOrgId: string | null
  onSwitchOrg: (orgId: string) => void
}) {
  const currentMembership = memberships.find(
    (membership) => membership.organizationId === currentOrgId
  )
  const currentOrg = currentMembership?.organization

  if (memberships.length <= 1) {
    return (
      <div className="border-border flex items-center gap-3 rounded-xl border px-3 py-3">
        <OrganizationAvatar name={currentOrg?.name} logo={currentOrg?.logo} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {currentOrg?.name ?? '—'}
          </p>
          <p className="text-muted-foreground text-xs">Organização atual</p>
        </div>
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="border-border hover:bg-accent/50 focus-visible:ring-ring flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition outline-none focus-visible:ring-2"
        >
          <OrganizationAvatar name={currentOrg?.name} logo={currentOrg?.logo} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {currentOrg?.name ?? '—'}
            </p>
            <p className="text-muted-foreground text-xs">
              Toque para trocar de organização
            </p>
          </div>
          <ChevronsUpDownIcon className="text-muted-foreground size-4 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-(--radix-dropdown-menu-trigger-width) min-w-64 rounded-xl"
      >
        <DropdownMenuLabel className="text-muted-foreground text-xs">
          Organizações
        </DropdownMenuLabel>
        {memberships.map((membership) => {
          const active = membership.organizationId === currentOrgId
          return (
            <DropdownMenuItem
              key={membership.organizationId}
              className="gap-2 p-2"
              onClick={() => onSwitchOrg(membership.organizationId)}
            >
              <OrganizationAvatar
                name={membership.organization.name}
                logo={membership.organization.logo}
                className="size-7 rounded-md"
              />
              <span className="min-w-0 flex-1 truncate">
                {membership.organization.name}
              </span>
              {active ? (
                <CheckIcon className="text-primary size-4 shrink-0" />
              ) : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function AjustesPage() {
  const { user, memberships, currentOrgId, switchOrg, signOut } = useAuth()
  const push = usePushNotifications(currentOrgId)

  return (
    <div className="lg:border-border flex min-h-0 flex-1 flex-col lg:mx-auto lg:w-full lg:max-w-2xl lg:border-x">
      <AppHeader title="Ajustes" />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex items-center gap-3 p-4">
          <Avatar className="size-12">
            {user?.image ? <AvatarImage src={user.image} alt="" /> : null}
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
              {getInitials(user?.name ?? user?.email)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate font-medium">{user?.name ?? 'Usuário'}</p>
            <p className="text-muted-foreground truncate text-sm">
              {user?.email}
            </p>
          </div>
        </div>

        <Separator />

        {push.supported ? (
          <>
            <div className="flex items-center gap-3 p-4">
              <BellIcon className="text-muted-foreground size-5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Notificações</p>
                <p className="text-muted-foreground text-xs">
                  {push.permission === 'denied'
                    ? 'Bloqueadas nas configurações do navegador'
                    : 'Avisos de novas mensagens'}
                </p>
              </div>
              <Switch
                checked={push.subscribed}
                disabled={push.busy || push.permission === 'denied'}
                onCheckedChange={(checked) =>
                  checked ? void push.enable() : void push.disable()
                }
                aria-label="Ativar notificações"
              />
            </div>
            <Separator />
          </>
        ) : null}

        <div className="p-4">
          <p className="text-muted-foreground mb-2 text-xs font-medium uppercase">
            Organização
          </p>
          <OrganizationSwitcher
            memberships={memberships}
            currentOrgId={currentOrgId}
            onSwitchOrg={switchOrg}
          />
        </div>

        <Separator />

        <div className="p-4">
          <Button
            variant="outline"
            className="text-destructive h-11 w-full justify-start gap-2"
            onClick={async () => {
              await signOut()
              window.location.href = '/entrar'
            }}
          >
            <LogOutIcon className="size-4" />
            Sair
          </Button>
        </div>
      </div>
    </div>
  )
}
